import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import DarkModeToggle from './components/DarkModeToggle';
import HeroSection from './sections/HeroSection';
import ErrorBoundary from './ErrorBoundary';
import Toast from './components/Toast';
import CookieConsent from './components/CookieConsent';
import TurnstileCaptcha from './components/TurnstileCaptcha';
import emailjs from '@emailjs/browser';
import { emailjsConfig } from './config/emailjs';
import { initScrollTracking, trackFormSubmission, trackPhoneCall, trackMessagingAppClick } from './utils/analytics';
import './App.css';

import { galleryImages } from './data/images';
import { servicesData } from './data/services';
import AnimatedLogo from './AnimatedLogo';
import { BASE_BUTTON_MOTION, hoverVariant } from './utils/buttonMotion';
import { Phone } from "@phosphor-icons/react";
import { preCacheGalleryImages } from './utils/cacheImages';
import { registerServiceWorker } from './utils/serviceWorker';
import './utils/debugOffline'; // Load debug utilities in development

const ParticlesBackground = lazy(() =>
  import('./components/ParticlesBackground').then((module) => ({
    default: module.default || module.ParticlesBackground,
  }))
);
const ServicesSection = lazy(() => import('./sections/ServicesSection'));
const GallerySection = lazy(() => import('./sections/GallerySection'));
const AboutSection = lazy(() => import('./sections/AboutSection'));
const ContactSection = lazy(() => import('./sections/ContactSection'));
const Footer = lazy(() => import('./sections/Footer'));
const CallModal = lazy(() => import('./components/CallModal'));
const AppPickerModal = lazy(() => import('./components/AppPickerModal'));

const SectionSkeleton = ({ label, darkMode, minHeight = '20rem' }) => (
  <section className="py-16 px-6" aria-busy="true" aria-live="polite">
    <div
      className={`max-w-6xl mx-auto rounded-3xl border transition-colors duration-300 animate-pulse ${
        darkMode ? 'border-white/10 bg-white/5' : 'border-blue-200/50 bg-white/80'
      }`}
      style={{ minHeight }}
    >
      <span className="sr-only">{`Loading ${label} section`}</span>
    </div>
  </section>
);

const DeferredSection = ({ id, children, darkMode, minHeight = '24rem', rootMargin = '0px 0px 200px' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const placeholderRef = useRef(null);

  useEffect(() => {
    if (isVisible) return;
    const target = placeholderRef.current;

    if (!target) {
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          setIsVisible(true);
          obs.disconnect();
        }
      });
    }, { rootMargin });

    observer.observe(target);

    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  if (isVisible) {
    return children;
  }

  return (
    <section
      id={id}
      ref={placeholderRef}
      className="py-16 px-6"
      aria-label={`${id}-placeholder`}
    >
      <div
        className={`max-w-6xl mx-auto rounded-3xl border transition-colors duration-300 animate-pulse ${
          darkMode ? 'border-white/10 bg-white/5' : 'border-blue-200/40 bg-white/70'
        }`}
        style={{ minHeight }}
      />
    </section>
  );
};

function App() {
  const reduce = useReducedMotion();
  const [darkMode, setDarkMode] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showParticles, setShowParticles] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info'
  });

  // Call modal state for desktop users
  const [callModal, setCallModal] = useState({
    isVisible: false,
    number: ''
  });

  // App picker modal state
  const [showAppPicker, setShowAppPicker] = useState(false);

  // Turnstile CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, isVisible: false });
  };

  // Performance optimization state (to be implemented in future iteration)
  useEffect(() => {
    // Monitor performance metrics
    const logPerformance = () => {
      if (process.env.NODE_ENV === 'development' && performance.memory) {
        console.log('Memory usage:', {
          used: Math.round(performance.memory.usedJSHeapSize / 1048576),
          total: Math.round(performance.memory.totalJSHeapSize / 1048576),
          limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576)
        });
      }
    };

    const performanceTimer = setInterval(logPerformance, 30000);
    return () => clearInterval(performanceTimer);
  }, []);

  // Register service worker and pre-cache images for offline use
  useEffect(() => {
    // Register service worker
    registerServiceWorker().then((registration) => {
      if (registration && process.env.NODE_ENV === 'development') {
        console.log('Service worker registered successfully');
      }
    });

    // Initialize scroll depth tracking for analytics
    const cleanupScrollTracking = initScrollTracking();

    // Pre-cache gallery images after a short delay (to not block initial render)
    const preCacheTimer = setTimeout(() => {
      preCacheGalleryImages(galleryImages).then((success) => {
        if (success && process.env.NODE_ENV === 'development') {
          console.log('Gallery images cached for offline use');
        }
      });
    }, 2000); // Wait 2 seconds before starting to cache images

    return () => {
      clearTimeout(preCacheTimer);
      if (cleanupScrollTracking) {
        cleanupScrollTracking();
      }
    };
  }, []);

  // Defer particle background until browser is idle to avoid blocking first paint
  useEffect(() => {
    if (showParticles) {
      return;
    }

    if (typeof window === 'undefined') {
      setShowParticles(true);
      return;
    }

    let idleId;
    let timerId;

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => setShowParticles(true), { timeout: 1200 });
    } else {
      timerId = window.setTimeout(() => setShowParticles(true), 400);
    }

    return () => {
      if (idleId && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [showParticles]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Input sanitization function - allows spaces but prevents XSS
  const sanitizeInput = (input) => {
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/[<>]/g, '');
  };

  // Enhanced form validation
  const validateForm = () => {
    // Trim values for validation
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const message = formData.message.trim();

    // Name validation
    if (!name) {
      showToast('Please enter your name', 'error');
      return false;
    }
    if (name.length < 2) {
      showToast('Name must be at least 2 characters long', 'error');
      return false;
    }
    if (name.length > 50) {
      showToast('Name cannot exceed 50 characters', 'error');
      return false;
    }

    // Email validation
    if (!email) {
      showToast('Please enter your email address', 'error');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast('Please enter a valid email address (e.g., example@email.com)', 'error');
      return false;
    }
    if (email.length > 50) {
      showToast('Email cannot exceed 50 characters', 'error');
      return false;
    }

    // Phone validation
    if (!phone) {
      showToast('Please enter your phone number', 'error');
      return false;
    }
    const phoneRegex = /^[\d\s\-+()]{10,}$/;
    if (!phoneRegex.test(phone)) {
      showToast('Please enter a valid phone number (at least 10 digits)', 'error');
      return false;
    }
    if (phone.length > 50) {
      showToast('Phone number cannot exceed 50 characters', 'error');
      return false;
    }

    // Message validation
    if (!message) {
      showToast('Please enter your message', 'error');
      return false;
    }
    if (message.length < 10) {
      showToast('Message must be at least 10 characters long', 'error');
      return false;
    }
    if (message.length > 500) {
      showToast('Message cannot exceed 500 characters', 'error');
      return false;
    }

    return true;
  };

  const handleInputChange = (field, value) => {
    let sanitizedValue = sanitizeInput(value);
    
    // Phone number specific filtering: only allow numbers, spaces, +, -, (, )
    if (field === 'phone') {
      sanitizedValue = sanitizedValue.replace(/[^0-9\s+\-()]/g, '');
    }
    
    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Check CAPTCHA verification
    if (!captchaToken) {
      showToast(
        'Please complete the CAPTCHA verification to send your message',
        'error'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const { serviceId, templateId, publicKey } = emailjsConfig;
      const templateParams = {
        from_name: formData.name.trim(),
        from_email: formData.email.trim(),
        from_phone: formData.phone.trim(),
        message: formData.message.trim(),
        to_name: 'Just One Computer Repair',
        timestamp: new Date().toLocaleString(),
        captcha_token: captchaToken // Include captcha token for backend verification if needed
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      // Track successful form submission
      trackFormSubmission('contact_form', {
        form_location: 'contact_section',
        has_phone: !!formData.phone.trim(),
        captcha_verified: true,
      });
      
      showToast(
        'Thank you! Your message has been sent successfully.\nWe will get back to you as soon as we can.\n\nFor urgent queries, please call us at 0920 230 8881',
        'success'
      );
      
      // Clear form and reset captcha
      setFormData({ name: '', email: '', phone: '', message: '' });
      setCaptchaToken(null);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Email sending failed:', error);
      }
      showToast(
        'Failed to send message. Please try again or call us directly at 0920 230 8881',
        'error'
      );
      // Don't reset captcha on error - let user retry without re-verifying
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced call function with desktop modal support
  const callNumber = (number) => {
    // Track phone call click
    trackPhoneCall(number, 'call_button');
    
    // Check if device is mobile/tablet
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                     ('ontouchstart' in window) || 
                     (window.innerWidth <= 1024);
    
    if (isMobile) {
      // Mobile: Open phone dialer directly
      window.open(`tel:${number}`, '_self');
    } else {
      // Desktop: Show modal with options
      setCallModal({ isVisible: true, number });
    }
  };

  // Copy number to clipboard
  const copyToClipboard = async (number) => {
    try {
      await navigator.clipboard.writeText(number);
      showToast(`Phone number copied: ${number}`, 'success');
      setCallModal({ isVisible: false, number: '' });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = number;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        showToast(`Phone number copied: ${number}`, 'success');
      } catch (err) {
        showToast('Failed to copy number. Please copy manually.', 'error');
      }
      document.body.removeChild(textArea);
      setCallModal({ isVisible: false, number: '' });
    }
  };

  // Open app picker modal for desktop users to choose their preferred communication app
  const openWithApp = () => {
    setShowAppPicker(true);
  };

  // Open specific communication app with phone number
  const openSpecificApp = (app, number) => {
    const cleanNumber = number.replace(/[\s\-()]/g, ''); // Remove formatting
    
    // Track messaging app click
    const appName = app.charAt(0).toUpperCase() + app.slice(1);
    trackMessagingAppClick(appName, 'app_picker_modal');
    
    const appLinks = {
      viber: `viber://chat?number=${cleanNumber}`,
      whatsapp: `https://wa.me/${cleanNumber.startsWith('0') ? '63' + cleanNumber.substring(1) : cleanNumber}`,
      skype: `skype:${cleanNumber}?call`,
      teams: `https://teams.microsoft.com/l/call/0/0?users=${cleanNumber}`
    };

    if (appLinks[app]) {
      window.open(appLinks[app], '_blank');
      setShowAppPicker(false);
      setCallModal({ isVisible: false, number: '' });
      
      setTimeout(() => {
        showToast(`Opening ${appName}... If the app doesn't open, please install it first.`, 'info');
      }, 100);
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showAppPicker) {
          setShowAppPicker(false);
        } else if (callModal.isVisible) {
          setCallModal({ isVisible: false, number: '' });
        }
      }
    };
    
    if (callModal.isVisible || showAppPicker) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [callModal.isVisible, showAppPicker]);

  // Services data (imported from services.js)
  const services = servicesData;

  const images = galleryImages;

  return (
    <div className={`relative min-h-screen overflow-x-hidden transition-all duration-700 ${
      darkMode 
        ? "bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800" 
        : "bg-gradient-to-br from-gray-300 via-sky-200 to-sky-400"
    }`}>
      {/* Background */}
      <Suspense fallback={null}>
        {showParticles ? <ParticlesBackground darkMode={darkMode} /> : null}
      </Suspense>

      {/* Skip Navigation for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange-500 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 backdrop-blur-lg border-b border-white/10 ${
        darkMode ? "bg-slate-900/80" : "bg-white/20"
      }`} role="banner">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-2.5 sm:py-3 md:py-4 overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            {/* Logo - Fixed size, no stretching */}
            <motion.button
              onClick={() => document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' })}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-2 sm:gap-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 rounded-lg p-1.5 sm:p-2 flex-shrink-0"
              whileHover={{ scale: 1.02, transition: { duration: 0.3, ease: "easeOut" } }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex-shrink-0 scale-90 sm:scale-100">
                <AnimatedLogo />
              </div>
              <div className="text-left">
                <h1 className={`text-sm sm:text-base md:text-lg lg:text-xl font-bold leading-tight ${darkMode ? "text-white" : "text-blue-950"}`}>
                  Just One
                </h1>
                <p className={`text-xs sm:text-sm md:text-sm leading-tight ${darkMode ? "text-gray-200" : "text-blue-800"}`}>
                  Computer Repair
                </p>
              </div>
            </motion.button>

            {/* Desktop Navigation - Hidden on tablet and mobile */}
            <nav className="hidden lg:flex items-center gap-2 xl:gap-4" aria-label="Main navigation">
              {['Home', 'Services', 'Gallery', 'About', 'Contact'].map((item) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  aria-label={`Navigate to ${item} section`}
                  className={`relative px-4 xl:px-6 py-2.5 xl:py-3 font-medium text-sm xl:text-base rounded-xl border transition-all duration-300 overflow-hidden group ${
                    darkMode 
                      ? "bg-slate-800 border-slate-600 text-gray-300 hover:text-white" 
                      : "bg-white/75 backdrop-blur-md border-blue-300 text-blue-900 hover:text-blue-950 font-semibold"
                  } transform hover:scale-105`}
                  {...BASE_BUTTON_MOTION}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-400/0 to-orange-500/0 group-hover:from-orange-500/40 group-hover:via-orange-400/60 group-hover:to-orange-500/40 transition-all duration-500 rounded-xl" />
                  <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute inset-0 rounded-xl border-2 border-orange-400/80 animate-pulse" />
                  </div>
                  <span className="relative z-10">{item}</span>
                </motion.a>
              ))}
            </nav>

            {/* Right side buttons */}
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4">
              <DarkModeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              
              {/* Call button - Hidden on mobile and tablet, shown on desktop */}
              <motion.button
                onClick={() => callNumber("09202308881")}
                aria-label="Call us at 0920 230 8881"
                className="hidden lg:flex items-center gap-2 px-4 xl:px-6 py-2.5 xl:py-3 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white rounded-xl font-bold text-sm xl:text-base shadow-lg border border-orange-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/25 hover:bg-gradient-to-tl whitespace-nowrap"
                {...hoverVariant({ whileHover: { scale: 1.02, y: -2 } }, reduce)}
              >
                <Phone size={18} weight="fill" className="text-orange-200 xl:w-5 xl:h-5" />
                <span className="hidden xl:inline">Call Now</span>
                <span className="xl:hidden">Call</span>
              </motion.button>

              {/* Mobile/Tablet Menu Button - Shown on tablet and mobile */}
              <motion.button
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={menuOpen ? "Close mobile menu" : "Open mobile menu"}
                aria-expanded={menuOpen}
                className={`lg:hidden p-2.5 sm:p-3 rounded-xl border transition-all duration-300 overflow-hidden group ${
                  darkMode 
                    ? "bg-gradient-to-br from-slate-800/50 to-slate-700/50 border-slate-600/50 text-gray-300" 
                    : "bg-gradient-to-br from-white/30 to-white/20 border-white/30 text-slate-800"
                } backdrop-blur-md transform hover:scale-105`}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  background: darkMode 
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.6) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 100%)",
                  boxShadow: darkMode
                    ? "inset 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                    : "inset 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                }}
              >
                {/* Orange hover effect - Light Mode ONLY */}
                {!darkMode && (
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-400/0 to-orange-500/0 group-hover:from-orange-500/40 group-hover:via-orange-400/60 group-hover:to-orange-500/40 transition-all duration-500 rounded-xl" />
                )}
                
                <div className="relative z-10 flex flex-col space-y-1">
                  <motion.span className={`w-5 h-0.5 ${darkMode ? "bg-white" : "bg-slate-900"} transition-all duration-300`} animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 6 : 0 }} />
                  <motion.span className={`w-5 h-0.5 ${darkMode ? "bg-white" : "bg-slate-900"} transition-all duration-300`} animate={{ opacity: menuOpen ? 0 : 1 }} />
                  <motion.span className={`w-5 h-0.5 ${darkMode ? "bg-white" : "bg-slate-900"} transition-all duration-300`} animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -6 : 0 }} />
                </div>
              </motion.button>
            </div>
          </div>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="lg:hidden mt-3 md:mt-4 overflow-hidden"
              >
                <nav className={`backdrop-blur-md rounded-xl border border-white/20 p-3 md:p-4 ${darkMode ? "bg-white/10" : "bg-white/20"}`} aria-label="Mobile navigation">
                  {['Home', 'Services', 'Gallery', 'About', 'Contact'].map((item) => {
                    const target = `#${item.toLowerCase()}`;
                    return (
                      <motion.a
                        key={item}
                        href={target}
                        onClick={() => setMenuOpen(false)}
                        aria-label={`Navigate to ${item} section`}
                        className={`relative block w-full py-3 px-4 text-sm md:text-base text-left rounded-lg transition-all duration-300 mb-2 last:mb-0 overflow-hidden group ${
                          darkMode 
                            ? "text-white hover:bg-white/20" 
                            : "text-blue-900 font-semibold hover:bg-white/30"
                        }`}
                        whileHover={{ x: 10 }}
                      >
                        {/* Orange hover effect - Light Mode ONLY */}
                        {!darkMode && (
                          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-400/0 to-orange-500/0 group-hover:from-orange-500/40 group-hover:via-orange-400/60 group-hover:to-orange-500/40 transition-all duration-500 rounded-lg" />
                        )}
                        
                        <span className="relative z-10">{item}</span>
                      </motion.a>
                    );
                  })}
                  <motion.button
                    onClick={() => {
                      callNumber("09202308881");
                      setMenuOpen(false);
                    }}
                    aria-label="Call us at 0920 230 8881"
                    className="w-full mt-3 md:mt-4 py-3 px-4 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white rounded-lg md:rounded-xl font-bold text-sm md:text-base shadow-lg border border-orange-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/25 hover:bg-gradient-to-tl flex items-center justify-center gap-2"
                    {...hoverVariant({ whileHover: { scale: 1.02, y: -2 } }, reduce)}
                  >
                    <Phone size={18} weight="fill" className="text-orange-200 md:hidden" />
                    <Phone size={20} weight="fill" className="text-orange-200 hidden md:inline" />
                    Call Now
                  </motion.button>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="relative">
        <HeroSection darkMode={darkMode} callNumber={callNumber} />

        <DeferredSection id="services" darkMode={darkMode} minHeight="28rem">
          <Suspense fallback={<SectionSkeleton label="Services" darkMode={darkMode} minHeight="28rem" />}>
            <ServicesSection darkMode={darkMode} services={services} />
          </Suspense>
        </DeferredSection>

        <DeferredSection id="gallery" darkMode={darkMode} minHeight="32rem">
          <ErrorBoundary>
            <Suspense fallback={<SectionSkeleton label="Gallery" darkMode={darkMode} minHeight="32rem" />}>
              <GallerySection darkMode={darkMode} images={images} />
            </Suspense>
          </ErrorBoundary>
        </DeferredSection>

        <DeferredSection id="about" darkMode={darkMode} minHeight="24rem">
          <Suspense fallback={<SectionSkeleton label="About" darkMode={darkMode} minHeight="24rem" />}>
            <AboutSection darkMode={darkMode} />
          </Suspense>
        </DeferredSection>

        <DeferredSection id="contact" darkMode={darkMode} minHeight="28rem">
          <ErrorBoundary>
            <Suspense fallback={<SectionSkeleton label="Contact" darkMode={darkMode} minHeight="28rem" />}>
              <ContactSection
                darkMode={darkMode}
                formData={formData}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                callNumber={callNumber}
                captchaToken={captchaToken}
                onCaptchaVerify={setCaptchaToken}
                TurnstileCaptcha={TurnstileCaptcha}
              />
            </Suspense>
          </ErrorBoundary>
        </DeferredSection>

        <Suspense fallback={<footer className="py-10 text-center text-sm text-gray-400">Loading footer...</footer>}>
          <Footer darkMode={darkMode} />
        </Suspense>

        {/* Floating Action Button */}
        <motion.button
          onClick={() => callNumber("09202308881")}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full shadow-2xl border-2 border-white/20 backdrop-blur-sm z-40 flex items-center justify-center"
          {...hoverVariant({ whileHover: { scale: 1.1, y: -5 } }, reduce)}
          animate={{ 
            y: [0, -10, 0],
            boxShadow: [
              "0 10px 25px rgba(249, 115, 22, 0.3)",
              "0 20px 40px rgba(249, 115, 22, 0.4)",
              "0 10px 25px rgba(249, 115, 22, 0.3)"
            ]
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          aria-label="Call us at 0920 230 8881"
        >
          <Phone size={28} weight="fill" />
        </motion.button>
      </main>

      {/* Call Modal - Desktop Only */}
      <AnimatePresence>
        {callModal.isVisible && (
          <Suspense fallback={null}>
            <CallModal
              darkMode={darkMode}
              number={callModal.number}
              onClose={() => setCallModal({ isVisible: false, number: '' })}
              onCopyNumber={() => copyToClipboard(callModal.number)}
              onOpenAppPicker={openWithApp}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* App Picker Modal - Choose Communication App */}
      <AnimatePresence>
        {showAppPicker && (
          <Suspense fallback={null}>
            <AppPickerModal
              darkMode={darkMode}
              number={callModal.number}
              onClose={() => setShowAppPicker(false)}
              onSelectApp={openSpecificApp}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={6000}
      />

      {/* Cookie Consent Banner */}
      <CookieConsent darkMode={darkMode} />
    </div>
  );
}

export default App;
